/**
 * German Card Template Renderer
 * Renders German markdown templates with YAML frontmatter for credit card articles
 */

import type { Database } from "@/integrations/supabase/types";
import {
  getCardCategory,
  getOverdraftRate,
  formatPercentDE,
  CARD_CATEGORY_SHORT_LABEL,
} from "@/lib/cardCategory";

type Card = Database["public"]["Tables"]["cards"]["Row"];

/**
 * Slugify text for German (handles umlauts)
 */
function slugify(text: string): string {
  return text
    .toLowerCase()
    .normalize("NFKD")
    .replace(/ä/g, "ae")
    .replace(/ö/g, "oe")
    .replace(/ü/g, "ue")
    .replace(/ß/g, "ss")
    .replace(/[^\w\s-]/g, "")
    .replace(/\s+/g, "-")
    .replace(/-+/g, "-")
    .replace(/^-|-$/g, "");
}

/**
 * Capitalize network names
 */
function capitalizeNetwork(network: string | null): string {
  if (!network) return "";
  const map: Record<string, string> = {
    visa: "Visa",
    mastercard: "Mastercard",
    american_express: "American Express",
  };
  return map[network.toLowerCase()] || network;
}

/**
 * Generate annual fee note for German template
 */
function generateAnnualFeeNote(fee: number | null): string {
  if (fee === 0) return " ohne Jahresgebühr";
  return "";
}

/**
 * Generate foreign fee note for German template
 */
function generateForeignFeeNote(percent: number | null): string {
  if (percent === 0) return "Kein Auslandseinsatzentgelt.";
  return "";
}

/**
 * Generate travel insurance block
 */
function generateTravelBlock(card: Card): string {
  const insurances = [];
  if (card.travel_health_insurance) insurances.push("Reisekrankenversicherung");
  if (card.travel_accident_insurance) insurances.push("Reiseunfallversicherung");
  if (card.travel_cancellation_insurance) insurances.push("Reiserücktrittsversicherung");
  if (card.travel_baggage_insurance) insurances.push("Reisegepäckversicherung");

  if (insurances.length === 0) {
    return "Keine Reiseversicherung enthalten.";
  }

  return `Inkludiert: ${insurances.join(", ")}.`;
}

/**
 * Generate benefits/highlights block
 */
function generateBenefitsBlock(card: Card): string {
  const benefits: string[] = [];

  // Add highlights from selling_points
  if (card.selling_points && Array.isArray(card.selling_points)) {
    benefits.push(...card.selling_points);
  }

  // Add discounts
  if (card.travel_discount_text) {
    benefits.push(card.travel_discount_text);
  }
  if (card.rental_car_discount_text) {
    benefits.push(card.rental_car_discount_text);
  }

  // Add cashback/bonus info
  if (card.cashback_program) {
    benefits.push("Cashback-Programm verfügbar");
  }
  if (card.bonus_program) {
    benefits.push("Bonusprogramm verfügbar");
  }

  if (benefits.length === 0) {
    return "Keine besonderen Vorteile angegeben.";
  }

  return benefits.map((b) => `- ${b}`).join("\n");
}

/**
 * Generate ATM summary (prefer international, then join available)
 */
function generateAtmSummary(card: Card): string {
  if (card.atm_international_text) {
    return card.atm_international_text;
  }

  const parts = [];
  if (card.atm_domestic_text) parts.push(`Inland: ${card.atm_domestic_text}`);
  if (card.atm_eurozone_text) parts.push(`Eurozone: ${card.atm_eurozone_text}`);
  if (card.atm_international_text) parts.push(`International: ${card.atm_international_text}`);

  return parts.length > 0 ? parts.join("; ") : "k. A.";
}

/**
 * Generate programmatic SEO tags
 */
function generateTags(card: Card): string[] {
  const tags: string[] = [];

  // Network
  if (card.network) {
    const networkSlug = card.network.toLowerCase().replace(/\s+/g, "-");
    tags.push(networkSlug);
  }

  // Type
  tags.push("kreditkarte");
  const tagCategory = getCardCategory(card);
  if (tagCategory === "debit") tags.push("debitkarte");
  if (tagCategory === "charge") tags.push("charge-karte");
  if (tagCategory === "prepaid") tags.push("prepaid-kreditkarte");

  // Features
  if (card.annual_fee === 0) tags.push("kostenlose-kreditkarte");

  const hasInsurance =
    card.travel_health_insurance ||
    card.travel_accident_insurance ||
    card.travel_cancellation_insurance ||
    card.travel_baggage_insurance;
  if (hasInsurance) tags.push("reiseversicherung");

  if (card.cashback_program) tags.push("cashback");
  if (card.bonus_program) tags.push("bonusprogramm");
  if (card.nfc_contactless) tags.push("kontaktlos");
  if (card.foreign_tx_fee_percent === 0) tags.push("kein-auslandseinsatzentgelt");

  // Issuer slug
  if (card.issuer) {
    tags.push(slugify(card.issuer));
  }

  return tags;
}

/**
 * Generate meta description line (short summary of key features)
 */
function generateMetaLine(card: Card): string {
  const features = [];

  if (card.annual_fee === 0) features.push("dauerhaft gebührenfrei");
  if (card.foreign_tx_fee_percent === 0) features.push("kostenlos im Ausland");
  if (card.nfc_contactless) features.push("kontaktlos");
  if (card.cashback_program || card.bonus_program) features.push("mit Bonus");

  const network = capitalizeNetwork(card.network);
  if (network) features.unshift(network);

  return features.join(", ");
}

/**
 * Extract discounts block from description field
 */
function extractDiscountsFromDescription(desc: string | null): string {
  if (!desc) return 'Keine Rabatte hinterlegt.';
  
  // Discounts are stored after "Reiseversicherung" section
  const parts = desc.split('\n\n');
  const discountPart = parts.find(p => 
    p.includes('Rabatte') || 
    p.includes('Discount') || 
    p.includes('5 %')
  );
  
  return discountPart || 'Keine Rabatte hinterlegt.';
}

/**
 * Generate intro paragraph for prepaid cards
 */
function generatePrepaidIntro(card: Card, displayConfig: any): string {
  const name = card.display_name || card.name;
  const bank = card.issuer;
  const network = capitalizeNetwork(card.network);
  const annualFee = card.annual_fee === 0 ? 'ohne Jahresgebühr' : `mit ${card.annual_fee} € Jahresgebühr`;
  
  // Extract FX fees by region from display_config
  const fxText = displayConfig?.fxTableText || '';
  const fxMatch = fxText.match(/DE\/EU:\s*([^•]+)•\s*International:\s*(.+)/);
  const fxDeEu = fxMatch?.[1]?.trim() || '0 %';
  const fxInt = fxMatch?.[2]?.trim() || 'k. A.';
  
  // Extract ATM min charge from display_config
  const atmText = displayConfig?.atmTableText || '';
  const atmMatch = atmText.match(/mind\.\s*([^,]+)/);
  const atmMin = atmMatch?.[1]?.trim() || 'k. A.';
  
  return `${name} von ${bank} ist eine ${network}-Prepaidkarte ${annualFee}. Als Prepaidkarte gibt es keinen Kreditrahmen und keine Sollzinsen. Auslandseinsatzentgelt: DE/EU ${fxDeEu}, international ${fxInt}. Bargeldabhebung: mind. ${atmMin}.`;
}

/**
 * Generate intro paragraph for credit cards
 */
function generateCreditIntro(card: Card, displayConfig: any): string {
  const name = card.display_name || card.name;
  const bank = card.issuer;
  const network = capitalizeNetwork(card.network);
  const annualFee = card.annual_fee === 0 ? 'ohne Jahresgebühr' : `mit ${card.annual_fee} € Jahresgebühr`;
  const category = getCardCategory(card);
  const typeLabel = CARD_CATEGORY_SHORT_LABEL[category];

  let intro = `${name} von ${bank} ist eine ${network}-${typeLabel} ${annualFee}.`;

  if (category === 'charge') {
    intro += ' Der Umsatz wird monatlich vollständig abgebucht – eine Teilzahlung und damit Sollzinsen gibt es nicht.';
  } else if (category === 'debit') {
    intro += ' Die Beträge werden direkt vom Girokonto abgebucht – es gibt keinen Kreditrahmen und keine Sollzinsen.';
    const overdraft = getOverdraftRate(card);
    if (overdraft !== null) {
      intro += ` Für eine Überziehung des Girokontos gilt ein Dispozins von ${formatPercentDE(overdraft)}.`;
    }
  } else if (card.apr_effective) {
    intro += ` Effektiver Jahreszins bei Teilzahlung: ${formatPercentDE(card.apr_effective)}.`;
  }

  return intro;
}

/**
 * Prepare all template variables for a German card
 */
export function prepareGermanCardData(card: Card) {
  const tags = generateTags(card);
  const network = capitalizeNetwork(card.network);
  const cardAny = card as any;
  const displayConfig = cardAny.display_config || {};
  const category = getCardCategory(card);
  const isPrepaid = category === "prepaid";
  const hasCreditMetrics = category === "revolving";

  return {
    // Basic card info
    product: card.display_name || card.name || "",
    bank: card.issuer || "",
    slug: card.slug || "",
    network: network,
    card_type: CARD_CATEGORY_SHORT_LABEL[category],
    affiliate_link: card.affiliate_link || card.apply_url || "#",
    images: {
      logo: card.logo_url || "",
    },

    // Fees & rates
    annual_fee_eur: card.annual_fee?.toString() || "0",
    apr_effective_percent: hasCreditMetrics
      ? (card.apr_effective?.toString() || "k. A.")
      : "–",
    foreign_tx_fee_percent: card.foreign_tx_fee_percent?.toString() || "k. A.",
    foreign_tx_fee_percent_label:
      card.foreign_tx_fee_percent !== null && card.foreign_tx_fee_percent !== undefined
        ? `${card.foreign_tx_fee_percent} %`
        : "k. A.",

    // Features
    nfc_label: card.nfc_contactless ? "Ja" : "Nein",

    // Generated blocks
    annual_fee_note: generateAnnualFeeNote(card.annual_fee),
    foreign_fee_note: generateForeignFeeNote(card.foreign_tx_fee_percent),
    travel_block: generateTravelBlock(card),
    benefits_block: generateBenefitsBlock(card),
    atm_summary: generateAtmSummary(card),

    // SEO
    tags: tags.join(", "),
    meta_line: generateMetaLine(card),
    
    // Intro paragraph (depends on card category)
    intro_paragraph: isPrepaid
      ? generatePrepaidIntro(card, displayConfig)
      : generateCreditIntro(card, displayConfig),

    // NEW FIELDS (Phase 6):
    card_status: cardAny.card_tier || 'Standard',

    repayment:
      category === 'prepaid'
        ? 'Prepaid (Aufladen nötig)'
        : category === 'debit'
          ? 'Direkte Belastung des Girokontos'
          : category === 'charge'
            ? 'Monatlicher Vollausgleich (keine Teilzahlung)'
            : 'Teilzahlung möglich',
    
    account_included: cardAny.current_account_included 
      ? 'Mit Girokonto' 
      : '–',
    
    hotline_label: cardAny.hotline_available 
      ? 'Ja (24/7 Notfall-Hotline)' 
      : '–',
    
    // Reuse display_config data:
    fx_table: displayConfig.fxTableText || 'k. A.',
    atm_table: displayConfig.atmTableText || 'k. A.',
    
    // Parse discounts from description:
    discounts_block: extractDiscountsFromDescription(card.description),
  };
}

/**
 * Render German card article with template
 * Replaces {{variable}} placeholders with actual values
 */
export function renderGermanCardArticle(template: string, card: Card): string {
  const data = prepareGermanCardData(card);

  let rendered = template;

  // Replace all variables
  Object.entries(data).forEach(([key, value]) => {
    const regex = new RegExp(`{{${key}}}`, "g");
    rendered = rendered.replace(regex, String(value));
  });

  // Handle nested variables (e.g., {{images.logo}})
  rendered = rendered.replace(/{{images\.logo}}/g, data.images.logo);

  // Non-revolving cards have no APR at all: remove empty interest statements
  if (getCardCategory(card) !== "revolving") {
    // Sentences like "Effektiver Jahreszins: **– %**." (markdown emphasis allowed)
    rendered = rendered.replace(/\s*\**Effektiver Jahreszins:\**\s*\**\s*–\s*%\**\.?/g, "");
    // Bullet/list lines like "- **Effektiver Jahreszins:** – %"
    rendered = rendered
      .split("\n")
      .filter((line) => !/Effektiver Jahreszins:?\**\s*\**\s*–/.test(line))
      .join("\n");
  }

  return rendered;
}

/**
 * Get list of available template variables
 */
export function getAvailableVariables(): string[] {
  return [
    "{{product}} - Card name",
    "{{bank}} - Issuer/bank name",
    "{{slug}} - URL slug",
    "{{network}} - Card network (Visa, Mastercard, etc.)",
    "{{card_type}} - Card type (credit, debit, charge)",
    "{{affiliate_link}} - Affiliate/apply URL",
    "{{images.logo}} - Logo URL",
    "{{annual_fee_eur}} - Annual fee in EUR",
    "{{apr_effective_percent}} - Effective APR %",
    "{{foreign_tx_fee_percent}} - Foreign transaction fee %",
    "{{foreign_tx_fee_percent_label}} - Foreign fee with % or 'k. A.'",
    "{{nfc_label}} - 'Ja' or 'Nein'",
    "{{annual_fee_note}} - ' ohne Jahresgebühr' if free, else empty",
    "{{foreign_fee_note}} - 'Kein Auslandseinsatzentgelt.' if 0%, else empty",
    "{{travel_block}} - Travel insurance summary",
    "{{benefits_block}} - Bullet list of benefits",
    "{{atm_summary}} - ATM fee summary",
    "{{tags}} - Comma-separated SEO tags",
    "{{meta_line}} - Short feature summary for meta description",
    "{{intro_paragraph}} - Prepaid-specific or credit intro paragraph",
    "{{card_status}} - Card tier (Gold, Standard, etc.)",
    "{{repayment}} - Teilzahlung möglich / Prepaid",
    "{{account_included}} - Mit Girokonto / –",
    "{{hotline_label}} - 24/7 Notfall-Hotline / –",
    "{{fx_table}} - Foreign exchange fees by region",
    "{{atm_table}} - ATM withdrawal fees by region",
    "{{discounts_block}} - Markdown bullets of discounts",
  ];
}

/**
 * Parse markdown with YAML frontmatter and return just the body
 */
export function parseMarkdownBody(markdownWithFrontmatter: string): string {
  // Remove YAML frontmatter (between --- markers)
  const parts = markdownWithFrontmatter.split(/^---\s*$/m);
  if (parts.length >= 3) {
    // Return everything after the second ---
    return parts.slice(2).join("---").trim();
  }
  return markdownWithFrontmatter;
}
